import React, { memo, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import { Form, Formik } from 'formik';
import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';

import {
  getCurrentCompanyId,
  getPayrollAddressValidationRollout,
} from 'selectors/session';

import BottomDrawer from 'features/bottomDrawers/BottomDrawer';
import DrawerFooter from 'features/team/components/DrawerFooter';
import DrawerHeader from 'features/team/components/DrawerHeader';
import TaxEntryForm from 'features/team/components/TaxEntryForm';
import { TAX_FORM_STEP } from 'features/team/components/TaxEntryForm/constants';
import {
  RESULT,
  TAX_ENTRY_I18N_PATH,
  TAX_ENTRY_STEPS,
} from 'features/team/constants';
import {
  selectOnCloseDrawerCallback,
  selectTaxEntryDrawerIsOpen,
  selectTaxEntryUserData,
} from 'features/team/selectors';
import { closeTaxEntryDrawer, openSuccessModal } from 'features/team/slice';
import {
  ADD_TEAM_PAYROLL_FLOW_STEP,
  getPayrollFlowStep,
} from 'features/teamView/tracking';

import Loader from 'components/Loader';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { useTrackUx, withUxRoot } from 'util/uxEvents';

import ExitModal from '../../components/ExitModal';
import submitPaymentMethod from '../../components/TaxEntryForm/actions/submitPaymentMethod';
import submitPersonalInformation from '../../components/TaxEntryForm/actions/submitPersonalInformation';
import submitTaxForm from '../../components/TaxEntryForm/actions/submitTaxForm';

import { initialValues, validationSchema } from './constants';

const { cx } = cxHelpers('TaxEntryDrawerContainer');

const TaxEntryDrawerContainer = ({
  animations,
  onCloseDrawer,
  onCloseDrawerCallback,
  userData,
  currentCompanyId,
  onOpenSuccessModal,
  payrollAddressValidationRollout,
}) => {
  const trackUx = useTrackUx();

  const [isExiting, setIsExiting] = useState(false);
  const [formData, setFormData] = useState(userData);
  const [isLoading, setIsLoading] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [currentStep, setCurrentStep] = useState(
    TAX_FORM_STEP.PERSONAL_INFORMATION
  );

  const currentTaxForm = useMemo(
    () =>
      formData.employee_forms
        ? formData.employee_forms.find(f => f.description === currentStep)
        : null,
    [formData, currentStep]
  );

  const secondaryActionText = useMemo(
    () =>
      currentTaxForm && !currentTaxForm.is_blocking
        ? toI18n(`${TAX_ENTRY_I18N_PATH}.skip_and_continue`)
        : null,
    [currentTaxForm]
  );

  const formSteps = useMemo(() => {
    const steps = TAX_ENTRY_STEPS;

    if (formData.employee_forms) {
      return [
        ...steps,
        ...formData.employee_forms
          .filter(form => !form.isSubmitted) // do not add forms that have already been submitted
          .map(form => form.description),
      ];
    } else if (formData.tax_classification === 'w2') {
      return [...steps, 'Federal W-4'];
    }

    return steps;
  }, [formData]);

  const footerButtonText =
    stepIndex === formSteps.length - 1
      ? toI18n('new_team_drawer.finalize_tax_entry')
      : toI18n('new_team_drawer.tax_entry_continue');

  const trackSubmissionUxEvents = useCallback(() => {
    trackUx(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
      button_text: footerButtonText,
      result: RESULT.SUCCESS,
      add_team_payroll_flow_step: getPayrollFlowStep(currentStep),
    });
  }, [currentStep, trackUx, footerButtonText]);

  const setNextStep = useCallback(() => {
    const nextStep = formSteps[stepIndex + 1];

    if (nextStep) {
      setCurrentStep(nextStep);
      setStepIndex(index => ++index);
    } else {
      onCloseDrawer();

      const fullName = formData.last_name
        ? capitalize(formData.first_name).concat(
            ' ',
            capitalize(formData.last_name)
          )
        : capitalize(formData.first_name);

      onOpenSuccessModal({
        fullName,
        title: toI18n(`${TAX_ENTRY_I18N_PATH}.payroll_success_title`),
        userId: formData.id,
        isPayrollSetupDone: true,
        onCloseDrawerCallback,
      });
    }
  }, [
    formData,
    onOpenSuccessModal,
    onCloseDrawer,
    stepIndex,
    formSteps,
    onCloseDrawerCallback,
  ]);

  const handleSecondaryAction = useCallback(() => {
    trackUx(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
      button_text: secondaryActionText,
      result: RESULT.SUCCESS,
      add_team_payroll_flow_step:
        currentStep === 'Federal W-4'
          ? ADD_TEAM_PAYROLL_FLOW_STEP.FEDERAL
          : ADD_TEAM_PAYROLL_FLOW_STEP.STATE,
    });
    setNextStep();
  }, [currentStep, secondaryActionText, setNextStep, trackUx]);

  const handleCloseDrawer = useCallback(() => {
    setIsExiting(true);
  }, [setIsExiting]);

  const handleCloseExitDialog = useCallback(() => {
    setIsExiting(false);
  }, [setIsExiting]);

  const handleDiscard = useCallback(() => {
    setIsExiting(false);
    onCloseDrawer();
  }, [onCloseDrawer, setIsExiting]);

  const handleFormSubmit = useCallback(
    (values, actions) => {
      setIsLoading(true);

      let promise;

      switch (currentStep) {
        case TAX_FORM_STEP.PERSONAL_INFORMATION:
          promise = submitPersonalInformation(
            formData,
            currentCompanyId,
            values,
            actions,
            payrollAddressValidationRollout
          );
          break;
        case TAX_FORM_STEP.PAYMENT_METHOD:
          promise = submitPaymentMethod(formData, values);
          break;
        default:
          promise = submitTaxForm(currentStep, formData, values);
      }

      promise
        .then(response => {
          setFormData(response); // update user data
          setNextStep(); // move to the next step
          trackSubmissionUxEvents();
        })
        .catch(() => null)
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      currentStep,
      formData,
      currentCompanyId,
      setNextStep,
      trackSubmissionUxEvents,
      payrollAddressValidationRollout,
    ]
  );

  const userFullName = formData?.first_name
    ? `${formData?.first_name}${
        formData?.last_name ? ' ' + formData.last_name : ''
      } `
    : null;

  const taxFormTypeFormatted = formData?.tax_classification
    ? ` (${toI18n(
        `${TAX_ENTRY_I18N_PATH}.type.${formData?.tax_classification}`
      )})`
    : '';

  const TaxDrawerHeader = props => (
    <DrawerHeader
      {...props}
      currentStep={currentStep}
      showStepper
      formSteps={formSteps}
      activeIndex={stepIndex}
      subtitle={userFullName + taxFormTypeFormatted}
      subtitleIsLoading={!formData}
    />
  );

  const TaxDrawerFooter = props => (
    <DrawerFooter {...props} currentStep={currentStep} payrollStep />
  );

  return (
    <>
      <Formik
        validationSchema={validationSchema(formData, currentStep)}
        initialValues={initialValues(formData, currentStep)}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {() => (
          <Form style={{ margin: 0 }}>
            <BottomDrawer
              top={37}
              animations={animations}
              footerActionIsLoading={isLoading}
              footerSecondaryActionText={secondaryActionText}
              onClickFooterSecondaryAction={handleSecondaryAction}
              onCancelClick={handleCloseDrawer}
              headerComponent={TaxDrawerHeader}
              footerComponent={TaxDrawerFooter}
              headerTitle={toI18n('new_team_drawer.tax_entry')}
              footerButtonText={footerButtonText}
            >
              {() => (
                <Box maxw={576} w="100%" m="auto" pt={24} className={cx()}>
                  <Loader centeredAbsolute isLoading={!formData}>
                    <TaxEntryForm
                      currentStep={currentStep}
                      userData={formData}
                      currentTaxForm={currentTaxForm}
                    />
                  </Loader>
                </Box>
              )}
            </BottomDrawer>
          </Form>
        )}
      </Formik>
      <ExitModal
        onDiscard={handleDiscard}
        onClose={handleCloseExitDialog}
        isOpen={isExiting}
        title={toI18n('new_team_drawer.exit_modal.title.add')} // Change to dynamic add/edit
        description={toI18n('new_team_drawer.exit_modal.description')}
        continueButton={toI18n('new_team_drawer.exit_modal.continue.add')} // Change to dynamic add/edit
        discardButton={toI18n('new_team_drawer.exit_modal.discard')}
      />
    </>
  );
};

TaxEntryDrawerContainer.propTypes = {
  userData: PropTypes.object,
  animations: PropTypes.object,
  isOpen: PropTypes.bool,
  currentCompanyId: PropTypes.number,
  onCloseDrawer: PropTypes.func.isRequired,
  onOpenSuccessModal: PropTypes.func.isRequired,
};

export default connect(
  state => ({
    userData: selectTaxEntryUserData(state),
    isOpen: selectTaxEntryDrawerIsOpen(state),
    currentCompanyId: getCurrentCompanyId(state),
    onCloseDrawerCallback: selectOnCloseDrawerCallback(state),
    payrollAddressValidationRollout: getPayrollAddressValidationRollout(state),
  }),
  {
    onCloseDrawer: closeTaxEntryDrawer,
    onOpenSuccessModal: openSuccessModal,
  }
)(
  withUxRoot({
    productArea: PRODUCT_AREAS.TEAM,
    eventCategory: EVENT_CATEGORIES.ADD_TEAM_PAYROLL_FLOW,
  })(memo(TaxEntryDrawerContainer))
);
